'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'next/navigation';

import '../../../app/hubspot-form-new.css';

type Locale = 'en' | 'fr' | 'es';

interface HubspotFormNewProps {
  portalId: string;
  formId: string;
  formTarget?: string;
  redirectUrl?: string;
  className?: string;
  region?: string;
  scriptSrc?: string;
  hubspotFormClassName?: string;
  hideRecaptcha?: boolean;
  submitButtonPosition?: 'left' | 'right';
  onFormReady?: () => void;
  onFormSubmitted?: () => void;
}

const getDeveloperScriptSrc = (portalId: string) => {
  return `https://js.hsforms.net/forms/embed/developer/${portalId}.js`;
};

const HubspotFormNew: React.FC<HubspotFormNewProps> = ({
  portalId,
  formId,
  redirectUrl,
  formTarget = 'hubspot-form-new',
  className = '',
  region = 'na1',
  scriptSrc,
  hubspotFormClassName = 'hs-form-html',
  hideRecaptcha = false,
  submitButtonPosition = 'left',
  onFormReady,
  onFormSubmitted,
}) => {
  const { locale } = useParams<{ locale?: Locale }>();
  const currentLocale: Locale = locale || 'en';

  const [formVisible, setFormVisible] = useState(false);
  const hasRevealedForm = useRef(false);

  const developerFormTarget = `${formTarget}-developer`;

  const localeRedirectURL = useMemo(() => {
    if (!redirectUrl) return undefined;

    const isExternalUrl = /^https?:\/\//.test(redirectUrl);

    if (isExternalUrl || currentLocale === 'en') {
      return redirectUrl;
    }

    const normalizedRedirectUrl = redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`;

    return `/${currentLocale}${normalizedRedirectUrl}`;
  }, [currentLocale, redirectUrl]);

  const revealForm = useCallback(() => {
    if (hasRevealedForm.current) return;

    hasRevealedForm.current = true;
    setFormVisible(true);
    onFormReady?.();
  }, [onFormReady]);

  const translateForm = useCallback(
    (formContainer: HTMLElement) => {
      const translations: Record<Locale, Record<string, string>> = {
        en: {},
        fr: {
          'First name': 'Prénom',
          'First Name': 'Prénom',
          'First Name*': 'Prénom',

          'Last name': 'Nom',
          'Last Name': 'Nom',
          'Last Name*': 'Nom',

          'Email address': 'Adresse e-mail',
          'Email Address': 'Adresse e-mail',
          'Email Address*': 'Adresse e-mail',
          Email: 'E-mail',
          'Email*': 'E-mail',

          'Phone number': 'Numéro de téléphone',
          'Phone Number': 'Numéro de téléphone',

          Organization: 'Organisation',
          Organisation: 'Organisation',
          'Organisation*': 'Organisation',
          'Company Name': 'Organisation',
          'Company Name*': 'Organisation',

          Country: 'Pays',
          'Country*': 'Pays',

          Region: 'Région',
          Message: 'Message',
          'Your message': 'Votre message',
          'Interested in': 'Intéressé par',
          'All Products': 'Tous les produits',
          Other: 'Autres',

          Search: 'Rechercher',
          Submit: 'Envoyer',
          'Please complete this required field.': 'Veuillez remplir ce champ obligatoire.',
        },
        es: {
          'First name': 'Nombre',
          'First Name': 'Nombre',
          'First Name*': 'Nombre',

          'Last name': 'Apellido',
          'Last Name': 'Apellido',
          'Last Name*': 'Apellido',

          'Email addres': 'Correo Electrónico',
          'Email address': 'Correo Electrónico',
          'Email Address': 'Correo Electrónico',
          'Email Address*': 'Correo Electrónico',
          Email: 'Correo Electrónico',
          'Email*': 'Correo Electrónico',

          'Phone number': 'Número de teléfono',
          'Phone Number': 'Número de teléfono',

          Organization: 'Organización',
          Organisation: 'Organización',
          'Organisation*': 'Organización',
          'Company Name': 'Organización',
          'Company Name*': 'Organización',

          Country: 'País',
          'Country*': 'País',

          Region: 'Región',
          Message: 'Mensaje',
          'Your message': 'Su mensaje',
          'Interested in': 'Interesado en',
          'All Products': 'Todos los productos',
          Other: 'Otros',

          Search: 'Buscar',
          Submit: 'Enviar',
          'Please complete this required field.': 'Rellene este campo obligatorio.',
        },
      };

      const placeholders: Record<Locale, Record<string, string>> = {
        en: {
          firstname: 'Enter your first name',
          lastname: 'Enter your last name',
          email: 'Enter your email address',
          company: 'Enter your company name',
          country: 'Select your country',
          phone: 'Enter your phone number',
          message: 'Enter your message',
          search: 'Search',
        },
        fr: {
          firstname: 'Saisissez votre prénom',
          lastname: 'Saisissez votre nom de famille',
          email: 'Saisissez votre adresse électronique',
          company: 'Entrez votre organisation',
          country: 'Veuillez sélectionner',
          phone: 'Saisissez votre numéro de téléphone',
          message: 'Votre message',
          search: 'Rechercher',
        },
        es: {
          firstname: 'Introduzca su nombre',
          lastname: 'Introduzca su apellido',
          email: 'Introduzca su dirección de correo electrónico',
          company: 'Introduzca su organización',
          country: 'Por favor seleccione',
          phone: 'Introduzca su número de teléfono',
          message: 'Su mensaje',
          search: 'Buscar',
        },
      };

      const currentTranslations = translations[currentLocale] || {};
      const currentPlaceholders = placeholders[currentLocale] || placeholders.en;

      const normalizeText = (value?: string | null) => {
        return value?.replace(/\*/g, '').trim().toLowerCase() || '';
      };

      const getFieldLabelText = (element: Element) => {
        const fieldWrapper =
          element.closest('[data-hsfc-id="TextField"]') ||
          element.closest('[data-hsfc-id="EmailField"]') ||
          element.closest('[data-hsfc-id="DropdownField"]') ||
          element.closest('[data-hsfc-id="PhoneField"]') ||
          element.closest('[data-hsfc-id="TextareaField"]');

        const label = fieldWrapper?.querySelector('[data-hsfc-id="FieldLabel"]');

        if (!label) return '';

        const labelSpans = Array.from(label.querySelectorAll('span'));

        const textSpan =
          labelSpans.find((span) => {
            const isRequiredIndicator = span.classList.contains(
              'hsfc-FieldLabel__RequiredIndicator'
            );
            const hasNestedSpan = Boolean(span.querySelector('span'));
            const hasText = Boolean(span.textContent?.trim());

            return !isRequiredIndicator && !hasNestedSpan && hasText;
          }) || label;

        return normalizeText(textSpan.textContent);
      };

      const getPlaceholderKey = (element: HTMLInputElement | HTMLTextAreaElement) => {
        const name = element.getAttribute('name') || '';
        const type = element.getAttribute('type') || '';
        const role = element.getAttribute('role') || '';
        const autocomplete = element.getAttribute('autocomplete') || '';
        const labelText = getFieldLabelText(element);

        if (
          type === 'hidden' ||
          type === 'submit' ||
          type === 'button' ||
          type === 'checkbox' ||
          type === 'radio'
        ) {
          return null;
        }

        if (role === 'searchbox') return 'search';

        if (
          name.includes('/firstname') ||
          autocomplete === 'given-name' ||
          labelText === 'first name'
        ) {
          return 'firstname';
        }

        if (
          name.includes('/lastname') ||
          autocomplete === 'family-name' ||
          labelText === 'last name'
        ) {
          return 'lastname';
        }

        if (
          name.includes('/email') ||
          type === 'email' ||
          autocomplete === 'email' ||
          labelText === 'email'
        ) {
          return 'email';
        }

        if (
          name.includes('/company') ||
          autocomplete === 'organization' ||
          labelText === 'company name' ||
          labelText === 'organization' ||
          labelText === 'organisation'
        ) {
          return 'company';
        }

        if (
          name.includes('/phone') ||
          type === 'tel' ||
          autocomplete === 'tel' ||
          labelText === 'phone number'
        ) {
          return 'phone';
        }

        if (name.includes('/country') || role === 'combobox' || labelText === 'country') {
          return 'country';
        }

        if (labelText === 'message' || labelText === 'your message') {
          return 'message';
        }

        return null;
      };

      formContainer.querySelectorAll('label').forEach((label) => {
        const labelSpans = Array.from(label.querySelectorAll('span'));

        const labelTextElement =
          labelSpans.find((span) => {
            const isRequiredIndicator = span.classList.contains(
              'hsfc-FieldLabel__RequiredIndicator'
            );
            const hasNestedSpan = Boolean(span.querySelector('span'));
            const hasText = Boolean(span.textContent?.trim());

            return !isRequiredIndicator && !hasNestedSpan && hasText;
          }) || label;

        const key = labelTextElement.textContent?.trim();

        if (key && currentTranslations[key]) {
          labelTextElement.textContent = currentTranslations[key];
        }
      });

      formContainer.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
        const placeholderKey = getPlaceholderKey(input);

        if (placeholderKey && currentPlaceholders[placeholderKey]) {
          input.setAttribute('placeholder', currentPlaceholders[placeholderKey]);
          return;
        }

        const existingPlaceholder = input.getAttribute('placeholder')?.trim();

        if (existingPlaceholder && currentTranslations[existingPlaceholder]) {
          input.setAttribute('placeholder', currentTranslations[existingPlaceholder]);
        }
      });

      formContainer.querySelectorAll<HTMLTextAreaElement>('textarea').forEach((textarea) => {
        const placeholderKey = getPlaceholderKey(textarea);

        if (placeholderKey && currentPlaceholders[placeholderKey]) {
          textarea.setAttribute('placeholder', currentPlaceholders[placeholderKey]);
          return;
        }

        const existingPlaceholder = textarea.getAttribute('placeholder')?.trim();

        if (existingPlaceholder && currentTranslations[existingPlaceholder]) {
          textarea.setAttribute('placeholder', currentTranslations[existingPlaceholder]);
        }
      });

      formContainer
        .querySelectorAll<HTMLInputElement>('input[role="searchbox"]')
        .forEach((searchInput) => {
          searchInput.setAttribute('placeholder', currentPlaceholders.search);
          searchInput.setAttribute('aria-label', currentPlaceholders.search);
        });

      formContainer.querySelectorAll('select option').forEach((option) => {
        const text = option.textContent?.trim();

        if (text && currentTranslations[text]) {
          option.textContent = currentTranslations[text];
        }
      });

      formContainer.querySelectorAll<HTMLInputElement>("input[type='submit']").forEach((button) => {
        const value = button.getAttribute('value')?.trim();

        if (value && currentTranslations[value]) {
          button.setAttribute('value', currentTranslations[value]);
        }
      });

      formContainer
        .querySelectorAll<HTMLButtonElement>('button[type="submit"], .hsfc-Button')
        .forEach((button) => {
          const text = button.textContent?.trim();

          if (text && currentTranslations[text]) {
            button.textContent = currentTranslations[text];
          }
        });

      formContainer
        .querySelectorAll(
          '.hs-error-msg, .hsfc-ErrorAlert, .hsfc-FieldError, .hsfc-NavigationRow__Alerts'
        )
        .forEach((errorElement) => {
          const text = errorElement.textContent?.trim();

          if (text && currentTranslations[text]) {
            errorElement.textContent = currentTranslations[text];
          }
        });
    },
    [currentLocale]
  );

  const applyFormOptions = useCallback(() => {
    if (!hideRecaptcha) return;

    const wrapper = document.getElementById(formTarget);

    if (!wrapper) return;

    const recaptchaElements = wrapper.querySelectorAll<HTMLElement>(
      [
        '[data-hsfc-id="ReCaptchaV2"]',
        '.hsfc-ReCaptchaV2',
        '.grecaptcha-badge',
        'iframe[title="reCAPTCHA"]',
        '#g-recaptcha-response',
        '#hs-recaptcha-response',
        'textarea[name="g-recaptcha-response"]',
        'input[name="g-recaptcha-response"]',
      ].join(',')
    );

    recaptchaElements.forEach((recaptchaElement) => {
      const recaptchaRow =
        recaptchaElement.closest<HTMLElement>('[data-hsfc-id="Row"]') ||
        recaptchaElement.closest<HTMLElement>('.hsfc-Row');

      const elementsToHide = [recaptchaRow, recaptchaElement];

      elementsToHide.forEach((element) => {
        if (!element) return;

        element.setAttribute('hidden', 'true');
        element.setAttribute('aria-hidden', 'true');
        element.classList.add('valdHsFormBlockNew__hiddenRecaptcha');

        element.style.setProperty('display', 'none', 'important');
        element.style.setProperty('visibility', 'hidden', 'important');
        element.style.setProperty('opacity', '0', 'important');
        element.style.setProperty('height', '0', 'important');
        element.style.setProperty('min-height', '0', 'important');
        element.style.setProperty('max-height', '0', 'important');
        element.style.setProperty('width', '0', 'important');
        element.style.setProperty('min-width', '0', 'important');
        element.style.setProperty('max-width', '0', 'important');
        element.style.setProperty('padding', '0', 'important');
        element.style.setProperty('margin', '0', 'important');
        element.style.setProperty('overflow', 'hidden', 'important');
        element.style.setProperty('pointer-events', 'none', 'important');
        element.style.setProperty('position', 'absolute', 'important');
        element.style.setProperty('z-index', '-1', 'important');
      });
    });
  }, [hideRecaptcha, formTarget]);

  useEffect(() => {
    if (!portalId || !formId) return;

    setFormVisible(false);
    hasRevealedForm.current = false;

    const formContainer = document.getElementById(developerFormTarget);

    if (!formContainer) return;

    formContainer.innerHTML = '';

    const revealWhenRendered = () => {
      const hasRenderedForm =
        formContainer.querySelector('form') ||
        formContainer.querySelector('.hsfc-Form') ||
        formContainer.querySelector('[data-hsfc-id="Form"]');

      if (!hasRenderedForm) return;

      window.setTimeout(() => {
        translateForm(formContainer);
        applyFormOptions();
        revealForm();
      }, 0);
    };

    const wrapper = document.getElementById(formTarget);

    const observer = new MutationObserver(() => {
      revealWhenRendered();
      applyFormOptions();
    });

    observer.observe(wrapper || formContainer, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    const handleSubmittedMessage = (event: MessageEvent) => {
      const data = event.data as {
        type?: string;
        eventName?: string;
        id?: string;
        formGuid?: string;
      };

      const submittedFormId = data.id || data.formGuid;

      const isHubSpotSubmitEvent =
        data?.type === 'hsFormCallback' && data?.eventName === 'onFormSubmitted';

      const isCurrentForm = !submittedFormId || submittedFormId === formId;

      if (isHubSpotSubmitEvent && isCurrentForm) {
        onFormSubmitted?.();

        if (localeRedirectURL) {
          window.location.href = localeRedirectURL;
        }
      }
    };

    window.addEventListener('message', handleSubmittedMessage);

    const developerScriptSrc = scriptSrc || getDeveloperScriptSrc(portalId);
    const scriptId = `${formTarget}-hubspot-new-form-script`;

    document.getElementById(scriptId)?.remove();

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = developerScriptSrc;
    script.defer = true;

    script.onload = () => {
      window.setTimeout(() => {
        translateForm(formContainer);
        applyFormOptions();
        revealWhenRendered();
      }, 300);

      window.setTimeout(applyFormOptions, 1000);
      window.setTimeout(applyFormOptions, 2000);
      window.setTimeout(applyFormOptions, 4000);
    };

    formContainer.before(script);

    return () => {
      observer.disconnect();
      script.remove();
      window.removeEventListener('message', handleSubmittedMessage);
    };
  }, [
    portalId,
    formId,
    formTarget,
    developerFormTarget,
    scriptSrc,
    translateForm,
    applyFormOptions,
    revealForm,
    onFormSubmitted,
    localeRedirectURL,
  ]);

  const wrapperClassName = [
    className,
    'valdHsFormBlockNew',
    hideRecaptcha ? 'valdHsFormBlockNew--hideRecaptcha' : '',
    submitButtonPosition === 'right'
      ? 'valdHsFormBlockNew--submitRight'
      : 'valdHsFormBlockNew--submitLeft',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      id={formTarget}
      className={wrapperClassName}
      style={{
        minHeight: '560px',
        visibility: formVisible ? 'visible' : 'hidden',
        opacity: formVisible ? 1 : 0,
      }}
    >
      <div
        id={developerFormTarget}
        className={hubspotFormClassName}
        data-region={region}
        data-form-id={formId}
        data-portal-id={portalId}
      />
    </div>
  );
};

export default HubspotFormNew;
